import { useMemo, useState } from 'react';
import {
  hostMonthGrid, lastMonths, money, sortHosts, totals, STAGES,
  type HostHealth, type HostSort, type HostStat,
} from '../../lib/hostAnalytics';
import { STAGE_LABEL, type Journey } from '../../lib/tripPipeline';

// Hand-drawn SVG, no chart library. Same stage colours as the Trips dashboard.
const STAGE_COLOR: Record<string, string> = {
  chat: '#c4b5fd', payment: '#a78bfa', handbook: '#8b5cf6',
  trip_started: '#7c3aed', trip_completed: '#6d28d9', survey_answered: '#4c1d95',
};
const HEALTH_LABEL: Record<HostHealth, string> = { ok: 'Healthy', watch: 'Watch', risk: 'At risk' };
const HEALTH_CLS: Record<HostHealth, string> = {
  ok: 'bg-success/10 text-success border-success/25',
  watch: 'bg-[rgba(255,153,59,.12)] text-gold border-[rgba(255,153,59,.24)]',
  risk: 'bg-crimson/10 text-crimson border-crimson/25',
};

function Tile({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-xl border border-app-border bg-app-card px-4 py-3">
      <div className="text-[11px] text-app-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-app-fg">{value}</div>
      {hint && <div className="text-[11px] text-app-muted">{hint}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-app-border bg-app-card p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-app-muted">{title}</h3>
      {children}
    </section>
  );
}

function MiniBar({ byStage, trips }: { byStage: Record<string, number>; trips: number }) {
  if (trips === 0) return <span className="text-app-muted">—</span>;
  return (
    <div className="flex h-2 w-28 overflow-hidden rounded-full bg-app-border" role="img"
      aria-label={STAGES.filter((s) => byStage[s]).map((s) => `${STAGE_LABEL[s]} ${byStage[s]}`).join(', ')}>
      {STAGES.map((s) => byStage[s] ? (
        <div key={s} style={{ width: `${(byStage[s] / trips) * 100}%`, background: STAGE_COLOR[s] }} title={`${STAGE_LABEL[s]}: ${byStage[s]}`} />
      ) : null)}
    </div>
  );
}

const first = (n: string) => (n.length > 14 ? `${n.slice(0, 13)}…` : n);

/** Trips per host, stacked by stage, top ten. */
function StackedHosts({ stats }: { stats: HostStat[] }) {
  const top = stats.filter((s) => s.trips > 0).slice(0, 10);
  const max = Math.max(1, ...top.map((s) => s.trips));
  const rowH = 22, labelW = 96, W = 520;
  return (
    <svg viewBox={`0 0 ${W} ${Math.max(top.length, 1) * rowH + 6}`} role="img" aria-label="Trips per host by stage" className="w-full text-app-fg">
      {top.map((s, i) => {
        let x = labelW;
        return (
          <g key={s.host_id} transform={`translate(0 ${i * rowH + 3})`}>
            <text x={labelW - 6} y={13} textAnchor="end" fontSize={10} fill="currentColor">{first(s.name)}</text>
            {STAGES.map((st) => {
              const n = s.byStage[st] ?? 0;
              if (!n) return null;
              const w = (n / max) * (W - labelW - 24);
              const r = <rect key={st} x={x} y={2} width={w} height={rowH - 8} fill={STAGE_COLOR[st]}><title>{`${s.name} · ${STAGE_LABEL[st]}: ${n}`}</title></rect>;
              x += w;
              return r;
            })}
            <text x={x + 4} y={13} fontSize={10} fill="currentColor" opacity={0.7}>{s.trips}</text>
          </g>
        );
      })}
    </svg>
  );
}

const monthLabel = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' });

/** Trips per month across all hosts. */
function PerMonth({ journeys, months }: { journeys: Journey[]; months: string[] }) {
  const counts = months.map((m) => journeys.filter((j) => j.kind === 'trip' && j.host_id && j.stage !== 'exited' && j.stage !== 'abandoned' && !j.archived
    && (j.start_date ?? j.created_at).slice(0, 7) === m).length);
  const max = Math.max(1, ...counts);
  const W = 520, H = 150, L = 24, B = 20, T = 8, bw = (W - L) / months.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Trips per month" className="w-full text-app-fg">
      {[0, 0.5, 1].map((f) => {
        const y = T + (H - T - B) * (1 - f);
        return <g key={f}><line x1={L} x2={W} y1={y} y2={y} stroke="currentColor" strokeOpacity={0.12} />
          <text x={L - 4} y={y + 3} textAnchor="end" fontSize={9} fill="currentColor" opacity={0.6}>{Math.round(max * f)}</text></g>;
      })}
      {months.map((m, i) => {
        const h = (counts[i] / max) * (H - T - B);
        return (
          <g key={m}>
            <rect x={L + bw * i + bw * 0.15} y={H - B - h} width={bw * 0.7} height={h} fill="#8b5cf6"><title>{`${m}: ${counts[i]}`}</title></rect>
            <text x={L + bw * i + bw / 2} y={H - 6} textAnchor="middle" fontSize={9} fill="currentColor" opacity={0.6}>{monthLabel(m)}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Response time (x) against rating (y); bubble size is the number of trips. */
function Bubbles({ stats }: { stats: HostStat[] }) {
  const pts = stats.filter((s) => s.rating !== null && s.responseDays !== null);
  const W = 520, H = 200, L = 30, B = 24, T = 10, R = 16;
  const maxX = Math.max(1, ...pts.map((p) => p.responseDays as number)) * 1.1;
  const maxT = Math.max(1, ...pts.map((p) => p.trips));
  const x = (v: number) => L + (v / maxX) * (W - L - R);
  const y = (v: number) => T + (1 - (v - 1) / 4) * (H - T - B);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Rating against response time, sized by trips" className="w-full text-app-fg">
      {[1, 2, 3, 4, 5].map((v) => (
        <g key={v}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="currentColor" strokeOpacity={0.12} />
          <text x={L - 4} y={y(v) + 3} textAnchor="end" fontSize={9} fill="currentColor" opacity={0.6}>{v}</text></g>
      ))}
      <text x={W - R} y={H - 6} textAnchor="end" fontSize={9} fill="currentColor" opacity={0.6}>days to confirm →</text>
      {pts.length === 0 && <text x={W / 2} y={H / 2} textAnchor="middle" fontSize={11} fill="currentColor" opacity={0.6}>Not enough ratings yet</text>}
      {pts.map((p) => (
        <circle key={p.host_id} cx={x(p.responseDays as number)} cy={y(p.rating as number)} r={5 + (p.trips / maxT) * 9}
          fill={p.health === 'risk' ? '#dc2626' : p.health === 'watch' ? '#ff993b' : '#8b5cf6'} fillOpacity={0.55}>
          <title>{`${p.name}: ${p.rating} stars, ${(p.responseDays as number).toFixed(1)}d, ${p.trips} trips`}</title>
        </circle>
      ))}
    </svg>
  );
}

/** Hosts (rows) by month (columns), darker = more trips. */
function Heatmap({ stats, journeys, months }: { stats: HostStat[]; journeys: Journey[]; months: string[] }) {
  const top = stats.filter((s) => s.trips > 0).slice(0, 10);
  const grid = hostMonthGrid(journeys, top.map((s) => s.host_id), months);
  const max = Math.max(1, ...grid.flat());
  const labelW = 96, cell = (520 - labelW) / months.length, rowH = 20;
  return (
    <svg viewBox={`0 0 520 ${top.length * rowH + 22}`} role="img" aria-label="Trips per host per month" className="w-full text-app-fg">
      {months.map((m, i) => (
        <text key={m} x={labelW + cell * i + cell / 2} y={12} textAnchor="middle" fontSize={9} fill="currentColor" opacity={0.6}>{monthLabel(m)}</text>
      ))}
      {top.map((s, r) => (
        <g key={s.host_id} transform={`translate(0 ${r * rowH + 18})`}>
          <text x={labelW - 6} y={13} textAnchor="end" fontSize={10} fill="currentColor">{first(s.name)}</text>
          {months.map((m, c) => (
            <rect key={m} x={labelW + cell * c + 1} y={1} width={cell - 2} height={rowH - 3} rx={3}
              fill="#6d28d9" fillOpacity={grid[r][c] ? 0.15 + 0.85 * (grid[r][c] / max) : 0.05}>
              <title>{`${s.name} · ${m}: ${grid[r][c]}`}</title>
            </rect>
          ))}
        </g>
      ))}
    </svg>
  );
}

/** How fast each host confirms, shortest first. */
function ResponseBars({ stats }: { stats: HostStat[] }) {
  const rows = stats.filter((s) => s.responseDays !== null).sort((a, b) => (a.responseDays as number) - (b.responseDays as number)).slice(0, 10);
  const max = Math.max(0.1, ...rows.map((s) => s.responseDays as number));
  if (rows.length === 0) return <p className="text-xs text-app-muted">No confirmed trips yet.</p>;
  return (
    <ul className="flex flex-col gap-1.5">
      {rows.map((s) => (
        <li key={s.host_id} className="flex items-center gap-2 text-xs">
          <span className="w-24 shrink-0 truncate text-app-fg">{s.name}</span>
          <div className="h-3 flex-1 rounded bg-app-bg"><div className="h-3 rounded bg-violet" style={{ width: `${((s.responseDays as number) / max) * 100}%` }} /></div>
          <span className="w-12 text-right text-app-muted">{(s.responseDays as number).toFixed(1)}d</span>
        </li>
      ))}
    </ul>
  );
}

export function HostDashboard({ stats, journeys }: { stats: HostStat[]; journeys: Journey[] }) {
  const [showMoney, setShowMoney] = useState(false);
  const [sort, setSort] = useState<HostSort>('trips');
  const [openOnly, setOpenOnly] = useState(false);
  const sorted = useMemo(() => sortHosts(stats, sort), [stats, sort]);
  const rows = openOnly ? sorted.filter((s) => s.health !== 'ok') : sorted;
  const t = useMemo(() => totals(stats), [stats]);
  const months = useMemo(() => lastMonths(8), []);
  const byTrips = useMemo(() => sortHosts(stats, 'trips'), [stats]);
  const gross = stats.reduce((n, s) => n + s.gross, 0);
  const commission = stats.reduce((n, s) => n + s.commission, 0);
  const th = 'px-3 py-2.5 text-left font-medium text-app-muted whitespace-nowrap';

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Tile label="Hosts" value={t.hosts} hint={`${t.withTrips} with trips`} />
        <Tile label="Trips" value={t.trips} hint="live, across hosts" />
        <Tile label="Average rating" value={t.rating === null ? '—' : `${t.rating} / 5`} />
        <Tile label="Needs attention" value={t.needsAttention} hint="hosts with a flagged trip" />
        <Tile label="At risk" value={t.atRisk} />
      </div>
      {showMoney && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Tile label="Paid by travelers" value={money(gross)} hint="net of refunds" />
          <Tile label="Commission" value={money(commission)} hint="kept by Sekretly, without VAT" />
          <Tile label="Paid to hosts" value={money(stats.reduce((n, s) => n + s.payout, 0))} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs">
        <label className="flex cursor-pointer items-center gap-1.5 text-app-fg">
          <input type="checkbox" checked={showMoney} onChange={(e) => setShowMoney(e.target.checked)} className="h-3.5 w-3.5 accent-violet" />
          Show money
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-app-fg">
          <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} className="h-3.5 w-3.5 accent-violet" />
          Only watch / at risk
        </label>
        <select aria-label="Sort hosts" value={sort} onChange={(e) => setSort(e.target.value as HostSort)}
          className="rounded-lg border border-app-border bg-app-card px-2.5 py-1.5 text-app-fg">
          <option value="trips">Most trips</option>
          <option value="health">Health (worst first)</option>
          <option value="rating">Best rated</option>
          <option value="response">Fastest to confirm</option>
          <option value="name">Name</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-app-border bg-app-card shadow-sm">
        <table className="w-full min-w-[820px] border-collapse text-xs">
          <thead>
            <tr className="border-b border-app-border bg-app-bg">
              <th className={th}>Host</th><th className={th}>Trips by stage</th><th className={th}>Trips</th><th className={th}>Done</th>
              <th className={th}>Rating</th><th className={th}>Recommend</th><th className={th}>Rebook</th><th className={th}>Confirms in</th><th className={th}>Health</th>
              {showMoney && <><th className={th}>Paid in</th><th className={th}>Payout</th></>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={showMoney ? 11 : 9} className="px-4 py-6 text-center text-app-muted">No hosts.</td></tr>}
            {rows.map((s) => (
              <tr key={s.host_id} className="border-b border-app-border last:border-0 hover:bg-app-bg/50">
                <td className="px-3 py-2.5">
                  <div className="font-medium text-app-fg">{s.name}{!s.active && <span className="ml-1 text-[10px] text-app-muted">(inactive)</span>}</div>
                  {s.city && <div className="text-[11px] text-app-muted">{s.city}</div>}
                </td>
                <td className="px-3 py-2.5"><MiniBar byStage={s.byStage} trips={s.trips} /></td>
                <td className="px-3 py-2.5 text-app-fg">{s.trips}</td>
                <td className="px-3 py-2.5 text-app-fg">{s.completed}</td>
                <td className="px-3 py-2.5 text-app-fg">{s.rating === null ? '—' : <>{s.rating}<span className="text-app-muted"> ({s.ratingCount})</span></>}</td>
                <td className="px-3 py-2.5 text-app-fg">{s.nps === null ? '—' : s.nps}</td>
                <td className="px-3 py-2.5 text-app-fg">{s.rebook.of ? `${s.rebook.repeat} of ${s.rebook.of}` : '—'}</td>
                <td className="px-3 py-2.5 text-app-fg">{s.responseDays === null ? '—' : `${s.responseDays.toFixed(1)}d`}</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex rounded-pill border px-2 py-0.5 text-[10px] font-medium ${HEALTH_CLS[s.health]}`}>{HEALTH_LABEL[s.health]}</span>
                </td>
                {showMoney && <><td className="px-3 py-2.5 text-app-fg">{money(s.gross)}</td><td className="px-3 py-2.5 text-app-fg">{money(s.payout)}</td></>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Trips per host, by stage">
          <StackedHosts stats={byTrips} />
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-app-muted">
            {STAGES.map((s) => <span key={s} className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm" style={{ background: STAGE_COLOR[s] }} />{STAGE_LABEL[s]}</span>)}
          </div>
        </Card>
        <Card title="Trips per month"><PerMonth journeys={journeys} months={lastMonths(12)} /></Card>
        <Card title="Rating vs. response time"><Bubbles stats={stats} /></Card>
        <Card title="Who is busy when"><Heatmap stats={byTrips} journeys={journeys} months={months} /></Card>
        <Card title="Fastest to confirm"><ResponseBars stats={stats} /></Card>
      </div>
    </div>
  );
}
